import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    InputLabel,
    Input,
    CircularProgress,
    Snackbar,
    Alert,
    Backdrop,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import iPaisaLoader from "../Assets/iPaisaLoaderKamran/iPaisaLoaderKamran.gif";
import { useNavigate } from 'react-router-dom';


const FileUploadComponent = () => {
    const [files, setFiles] = useState({});
    const [previews, setPreviews] = useState({});
    const [errors, setErrors] = useState({});
    const [uploading, setUploading] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: '' });
    const navigate = useNavigate(); // Initialize navigate hook
    const [isLoading, setIsLoading] = useState(false); // Add loading state
    const fileFields = [
        'shop_pan',
        'pan',
        'aadhaar',
        'certificateOfIncorporation',
        'tradeBusinessProof',
    ];

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const getFriendlyLabel = (label) =>
        label
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .replace(/certificate Of Incorporation/i, 'Certificate of Incorporation')
            .replace(/trade Business Proof/i, 'Trade/Business Proof');

    const handleFileChange = (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            setErrors(prev => ({
                ...prev,
                [fieldName]: 'Only PDF files are allowed.',
            }));
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            setErrors(prev => ({
                ...prev,
                [fieldName]: 'File size should not exceed 10 MB.',
            }));
            return;
        }

        // Clear error
        setErrors(prev => ({ ...prev, [fieldName]: '' }));

        // Set file
        setFiles(prev => ({ ...prev, [fieldName]: file }));

        // Generate preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviews(prev => ({ ...prev, [fieldName]: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
        const formData = new FormData();
        fileFields.forEach(field => {
            if (files[field]) {
                formData.append(field, files[field]);
            }
        });

        const jwtToken = sessionStorage.getItem('jwtToken');
        setUploading(true);
        setIsLoading(true); // Show loading GIF
        try {
            const response = await fetch(
                'https://api.ipaisa.co.in/myapp/cms/upload/merchant/docs',
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                    },
                    body: formData,
                }
            );

            const result = await response.json();
            if (response.ok) {
                setAlert({ open: true, message: 'Files uploaded successfully!', severity: 'success' });
                setFiles({});
                setPreviews({});

                // Set docUploaded to true in sessionStorage
                sessionStorage.setItem('docUploaded', 'TRUE');

                // Show loading GIF for 3 seconds then navigate
                setTimeout(() => {
                    setIsLoading(false); // Hide loading GIF
                    navigate('/cmslanding'); // Use navigate instead of window.location
                }, 3000);
            } else {
                setIsLoading(false); // Hide loading GIF if error
                setAlert({ open: true, message: result.message || 'Upload failed.', severity: 'error' });
            }
        } catch (error) {
            console.error('Upload failed:', error);
            setIsLoading(false); // Hide loading GIF if error
            setAlert({ open: true, message: 'Something went wrong!', severity: 'error' });
        } finally {
            setUploading(false);
        }
    };


    const handleReset = () => {
        const confirm = window.confirm('Are you sure you want to reset all selected files?');
        if (confirm) {
            setFiles({});
            setPreviews({});
            setErrors({});
        }
    };


    return (
        <Paper elevation={3} sx={{ padding: 4, maxWidth: 800, margin: 'auto', mt: 10 }}>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={isLoading}
            >
                <img src={iPaisaLoader} alt="Loading..." />
                <Typography sx={{ marginTop: "1%", fontSize: "16px" }}>
                    Processing...
                </Typography>
            </Backdrop>

            <Typography variant="h5" gutterBottom>
                Upload Documents
            </Typography>

            <Grid container spacing={3}>
                {fileFields.map(field => (
                    <Grid item xs={12} md={6} key={field}>
                        <Paper
                            elevation={2}
                            sx={{
                                padding: 2,
                                border: errors[field]
                                    ? '1px solid red'
                                    : files[field]
                                        ? '1px solid green'
                                        : '1px dashed grey',
                                borderRadius: 2,
                            }}
                        >
                            <InputLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                                {getFriendlyLabel(field)}
                            </InputLabel>
                            <Input
                                type="file"
                                inputProps={{ accept: '.pdf' }}
                                onChange={e => handleFileChange(e, field)}
                                fullWidth
                                sx={{ mb: 1 }}
                            />

                            <Box display="flex" alignItems="center" gap={1}>
                                {files[field] && (
                                    <Typography variant="body2" color="textSecondary">
                                        {files[field].name}
                                    </Typography>
                                )}
                                {files[field] && !errors[field] && (
                                    <CheckCircle fontSize="small" color="success" />
                                )}
                                {errors[field] && <ErrorOutline fontSize="small" color="error" />}
                            </Box>

                            {errors[field] && (
                                <Typography color="error" variant="body2" mt={1}>
                                    {errors[field]}
                                </Typography>
                            )}

                            {previews[field] && !errors[field] && (
                                <Box mt={2}>
                                    <Typography variant="body2" color="textSecondary">
                                        Preview:
                                    </Typography>
                                    <iframe
                                        src={previews[field]}
                                        title={field}
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            border: '1px solid #ccc',
                                            marginTop: '8px',
                                        }}
                                    />
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Box mt={4} display="flex" justifyContent="center" gap={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        uploading ||
                        Object.values(errors).some(err => err) ||
                        fileFields.some(field => !files[field])
                    }
                >
                    {uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload All'}
                </Button>


                <Button variant="outlined" color="secondary" onClick={handleReset}>
                    Reset
                </Button>
            </Box>

            {/* Snackbar Alert */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={alert.severity}
                    onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default FileUploadComponent;
